import React, { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  closeOnEscape?: boolean
  contentClassName?: string
  contentStyle?: React.CSSProperties
  overlayClassName?: string
  headerClassName?: string
  headerStyle?: React.CSSProperties
  titleClassName?: string
  bodyClassName?: string
  bodyStyle?: React.CSSProperties
  closeButtonClassName?: string
  closeButtonStyle?: React.CSSProperties
  footer?: React.ReactNode
  footerClassName?: string
  footerStyle?: React.CSSProperties
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnEscape = true,
  contentClassName = '',
  contentStyle,
  overlayClassName = '',
  headerClassName = '',
  headerStyle,
  titleClassName = '',
  bodyClassName = '',
  bodyStyle,
  closeButtonClassName = '',
  closeButtonStyle,
  footer,
  footerClassName = '',
  footerStyle
}) => {
  const sizeMap = useMemo(() => ({
    sm: '400px',
    md: '600px',
    lg: '800px',
    xl: '1200px',
    full: '95%'
  }), [])

  const modalWidth = size === 'full' ? sizeMap[size] : `min(90%, ${sizeMap[size]})`
  const dialogRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, closeOnEscape, onClose])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const previousActive = document.activeElement as HTMLElement | null
    const timer = window.setTimeout(() => {
      dialogRef.current?.focus()
    }, 0)

    return () => {
      window.clearTimeout(timer)
      previousActive?.focus?.()
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return createPortal(
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 ${overlayClassName}`.trim()} 
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] backface-hidden transform-gpu ${contentClassName}`.trim()}
        style={{
          width: modalWidth,
          maxWidth: '100%',
          ...contentStyle
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Modal'}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        {title ? (
          <div
            className={`flex items-center justify-between p-6 border-b border-gray-100 ${headerClassName}`.trim()}
            style={headerStyle}
          >
            <h2
              className={`text-xl font-bold text-gray-900 ${titleClassName}`.trim()}
            >
              {title}
            </h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className={`p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200 ${closeButtonClassName}`.trim()}
                aria-label="Fechar modal"
                style={closeButtonStyle}
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ) : null}

        <div
          className={`overflow-y-auto p-6 ${bodyClassName}`.trim()}
          style={bodyStyle}
        >
          {children}
        </div>

        {footer ? (
          <div
            className={`flex justify-end gap-3 p-6 border-t border-gray-100 ${footerClassName}`.trim()}
            style={footerStyle}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

export default Modal
